"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/utils/utils"
import { comboboxStyleDriver } from "@/utils/componentStyle"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "../container/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"

export interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxGroup {
  label: string
  options: ComboboxOption[]
}

export interface ComboboxProps {
  /** Array of options or grouped options */
  options: ComboboxOption[] | ComboboxGroup[]
  /** Currently selected value */
  value?: string
  /** Callback when value changes */
  onValueChange?: (value: string) => void
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Search input placeholder */
  searchPlaceholder?: string
  /** Empty state text when no options match search */
  emptyText?: string
  /** Whether the combobox is disabled */
  disabled?: boolean
  /** Custom className for the trigger */
  className?: string
  /** Custom className for the content */
  contentClassName?: string
  /** Loading state for async options */
  loading?: boolean
  /** Callback when search query changes (for async filtering) */
  onSearch?: (query: string) => void
  /** Accessible label for the combobox */
  "aria-label"?: string
  /** ID of element that labels the combobox */
  "aria-labelledby"?: string
}

function isGroupedOptions(options: ComboboxOption[] | ComboboxGroup[]): options is ComboboxGroup[] {
  return options.length > 0 && "options" in options[0]
}

/**
 * Combobox component for searchable select functionality
 * Built on Command + Popover primitives
 */
export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
  className,
  contentClassName,
  loading = false,
  onSearch,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const listboxId = React.useId()

  const handleSearch = React.useCallback(
    (query: string) => {
      setSearch(query)
      onSearch?.(query)
    },
    [onSearch]
  )

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      onValueChange?.(selectedValue === value ? "" : selectedValue)
      setOpen(false)
      setSearch("")
    },
    [value, onValueChange]
  )

  // Get the label for the current value
  const getSelectedLabel = (): string | undefined => {
    if (!value) return undefined

    if (isGroupedOptions(options)) {
      for (const group of options) {
        const found = group.options.find((opt) => opt.value === value)
        if (found) return found.label
      }
    } else {
      const found = options.find((opt) => opt.value === value)
      if (found) return found.label
    }
    return undefined
  }

  const selectedLabel = getSelectedLabel()

  // Filter options based on search
  const filterOptions = (opts: ComboboxOption[]): ComboboxOption[] => {
    if (!search || onSearch) return opts // If async search, don't filter locally
    return opts.filter((opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    )
  }

  const renderOptions = () => {
    if (isGroupedOptions(options)) {
      return options.map((group) => {
        const filteredOptions = filterOptions(group.options)
        if (filteredOptions.length === 0) return null
        return (
          <CommandGroup key={group.label} heading={group.label}>
            {filteredOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                onSelect={handleSelect}
                className={comboboxStyleDriver.item}
              >
                <Check
                  className={cn(
                    comboboxStyleDriver.itemCheck,
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )
      })
    }

    const filteredOptions = filterOptions(options)
    return filteredOptions.map((option) => (
      <CommandItem
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        onSelect={handleSelect}
        className={comboboxStyleDriver.item}
      >
        <Check
          className={cn(
            comboboxStyleDriver.itemCheck,
            value === option.value ? "opacity-100" : "opacity-0"
          )}
        />
        {option.label}
      </CommandItem>
    ))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          disabled={disabled}
          className={cn(
            comboboxStyleDriver.trigger,
            "justify-between",
            !value && "text-muted-foreground",
            className
          )}
        >
          {selectedLabel ?? placeholder}
          <ChevronsUpDown className={comboboxStyleDriver.triggerIcon} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(comboboxStyleDriver.content, "w-[--radix-popover-trigger-width] p-0", contentClassName)}
      >
        <Command shouldFilter={!onSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={handleSearch}
          />
          <CommandList id={listboxId} role="listbox" className={comboboxStyleDriver.list}>
            {loading ? (
              <div className={comboboxStyleDriver.loading}>
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty className={comboboxStyleDriver.empty}>
                  {emptyText}
                </CommandEmpty>
                {renderOptions()}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default Combobox
